'use client';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import NewCustomDataTable from '../../../components/common/NewCustomDataTable.jsx';
import { deleteQuiz, getQuizzesWithFilters } from '../../../../services/QuizService';
import { getMe } from '../../../../services/AuthService';
import { getcourses } from '../../../../services/CourseModule';
import { getTrainers, getTrainerByUserId } from '../../../../services/TrainerService';
import { useAuth } from '../../../../context/AuthContext';
import { toast } from '../../common/Toast/toast';
import NewCustomAsyncSelectDropdown from '../../common/NewCustomAsyncSelectDropdown.jsx';
import AddQuiz from './AddQuiz.jsx';
import Tip from '../ui/Tip.jsx';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import '../TrainerDashboard.css';
import debounce from 'lodash/debounce';
import moment from 'moment';
import FilterSheet from '../../common/FilterSheet/FilterSheet';
import { confirm } from '../../common/Confirm/confirm';
import PopupDone, { usePopupDone } from '../../common/PopupDone/PopupDone';

const ListQuiz = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // 100 a batch: the list scrolls rather than paging.
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  // Page 1 replaces the list; every page after it appends.
  const appendRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // The create/edit form, shown in a dialog rather than on its own
  // route. null = closed; { quizId } = editing; { quizId: null } = new.
  const [formModal, setFormModal] = useState(null);
  const [formStep, setFormStep] = useState(null);
  const listEndRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [actualTrainerId, setActualTrainerId] = useState('');
  const [isTrainer, setIsTrainer] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Store courses in a ref to avoid re-renders triggering API calls
  const coursesRef = useRef([]);
  
  // UI search state
  const [uiSearchTerm, setUiSearchTerm] = useState('');
  const [uiSelectedField, setUiSelectedField] = useState('all');
  
  // Applied search state
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [appliedSearchField, setAppliedSearchField] = useState('');
  
  // UI filter states
  const [uiCourseId, setUiCourseId] = useState('');
  const [uiTrainerId, setUiTrainerId] = useState('');
  
  // Applied filter states
  const [appliedCourseId, setAppliedCourseId] = useState('');
  const [appliedTrainerId, setAppliedTrainerId] = useState('');
  
  // Dropdown options with pagination
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  
  // Pagination state for dropdowns
  const [trainerPage, setTrainerPage] = useState(1);
  const [coursePage, setCoursePage] = useState(1);
  const [trainerHasMore, setTrainerHasMore] = useState(true);
  const [courseHasMore, setCourseHasMore] = useState(true);
  const [trainerLoading, setTrainerLoading] = useState(false);
  const [courseLoading, setCourseLoading] = useState(false);
  const [trainerSearch, setTrainerSearch] = useState('');
  const [courseSearch, setCourseSearch] = useState('');
  
  // Selected dropdown values
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  
  // Modal state
  const [showDetails, setShowDetails] = useState(false);
  const [detailsQuiz, setDetailsQuiz] = useState(null);
  // A save in the form popup, or a delete from the view popup, is confirmed
  // in that popup, which closes itself a few seconds later.
  const { done: formDone, showDone: showFormDone } = usePopupDone(Boolean(formModal));
  const { done: detailsDone } = usePopupDone(showDetails);
  
  const [trainerDataLoaded, setTrainerDataLoaded] = useState(false);
  // Refs
  const isFetching = useRef(false);
  const hasInitialized = useRef(false);
  const didFetchProfile = useRef(false);
  const [userRole, setUserRole] = useState(null);
  
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();

  useEffect(() => {
    if (!hasPermission('trainer-listquiz', 'view', 'lms')) {
      if (!hasShownToast.current) {
        toast.error('You do not have permission to view quizzes.');
        navigate('/');
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // Helper function to extract created by name
  const getCreatedByName = useCallback((createdBy) => {
    if (!createdBy) return 'N/A';
    
    // If it's an array
    if (Array.isArray(createdBy)) {
      const names = createdBy
        .map(creator => creator.trainerName || creator.name)
        .filter(name => name)
        .join(', ');
      return names || 'N/A';
    }
    
    // If it's a single object
    if (typeof createdBy === 'object') {
      return createdBy.trainerName || createdBy.name || 'N/A';
    }
    
    // If it's a string
    return createdBy;
  }, []);

  const fetchUserProfile = useCallback(async () => {
    if (didFetchProfile.current) return;
    didFetchProfile.current = true;

    try {
      const response = await getMe();
      const userData = response.data || response;
      if (userData.isSuperAdmin === true) {
        setUserRole('superadmin');
        setIsAdmin(true);
        setTrainerDataLoaded(true);
      } else {
        const profileLabel = userData.profiles?.[0]?.label?.toLowerCase();
        const isTrainerUser = profileLabel === 'trainer';
        setIsTrainer(isTrainerUser);
        setIsAdmin(false);
        
        if (isTrainerUser) {
          const userId = userData.id || userData._id || userData.userId;
          
          try {
            const trainerResponse = await getTrainerByUserId(userId);
            if (trainerResponse && trainerResponse.data) {
              const trainerObjectId = trainerResponse.data.id || trainerResponse.data._id;
              setActualTrainerId(trainerObjectId);
              console.log("Actual Trainer ID set to:", trainerObjectId);
              setTrainerDataLoaded(true);
            } else {
              setActualTrainerId(userId);
              
              setTrainerDataLoaded(true);
            }
          } catch (error) {
            console.error("Error fetching trainer data:", error);
            setActualTrainerId(userId);
          
            setTrainerDataLoaded(true);
          }
        } else {
          setTrainerDataLoaded(true);
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setTrainerDataLoaded(true);
    }
  }, []);

  // Fetch trainers with pagination and search
  const fetchTrainers = useCallback(async (page = 1, search = '', append = false) => {
    if (trainerLoading) return;
    setTrainerLoading(true);
    
    try {
      const limit = 20;
      const response = await getTrainers(limit, page, search);
      const trainersData = response.data || [];
      const pagination = response.pagination || {};
      
      const options = trainersData.map((trainer) => ({
        value: trainer.id,
        label: `${trainer.trainerName} | ${trainer.trainerEmail}`,
        email: trainer.trainerEmail,
      }));
      
      if (append) {
        setTrainerOptions(prev => [...prev, ...options]);
      } else {
        setTrainerOptions(options);
      }
      
      setTrainerHasMore(pagination.hasNext || pagination.hasNextPage || false);
      setTrainerPage(page);
      return options;
    } catch (error) {
      console.error('Error fetching trainers:', error);
      toast.error('Error loading trainers');
      return [];
    } finally {
      setTrainerLoading(false);
    }
  }, [trainerLoading]);

  // Fetch courses with pagination and search - store in ref
  const fetchCourses = useCallback(async (page = 1, search = '', append = false) => {
    if (courseLoading) return;
    setCourseLoading(true);
    
    try {
      const limit = 20;
      const response = await getcourses(limit, page, search);
      const coursesData = response.data || [];
      const pagination = response.pagination || {};
      
      const options = coursesData.map((course) => ({
        value: course.id,
        label: course.name,
      }));
      
      // Update courses ref
      if (page === 1 && !append) {
        coursesRef.current = coursesData;
      } else if (append) {
        coursesRef.current = [...coursesRef.current, ...coursesData];
      }
      
      if (append) {
        setCourseOptions(prev => [...prev, ...options]);
      } else {
        setCourseOptions(options);
      }
      
      setCourseHasMore(pagination.hasNext || pagination.hasNextPage || false);
      setCoursePage(page);
      return options;
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Error loading courses');
      return [];
    } finally {
      setCourseLoading(false);
    }
  }, [courseLoading]);

  // Fetch quizzes with server-side pagination and filters
  const fetchQuizzes = useCallback(async () => {
    if (isFetching.current) return;

    isFetching.current = true;

    // Read before anything awaits: a ref reset mid-flight would turn an
    // append into a replace and snap the list back to its first page.
    const isAppend = appendRef.current;
    try {
      if (isAppend) setLoadingMore(true);
      else setLoading(true);

      // Determine which trainer ID to use for createdBy filter
      let createdByFilter = '';
      if (isTrainer && actualTrainerId) {
        createdByFilter = actualTrainerId;
      } else if (isAdmin && appliedTrainerId) {
        createdByFilter = appliedTrainerId;
      }
      
      console.log('Fetching quizzes with params:', {
        limit: recordsPerPage,
        page: currentPage,
        search: appliedSearchTerm,
        searchField: appliedSearchField,
        courseId: appliedCourseId,
        createdBy: createdByFilter
      });
      
      const response = await getQuizzesWithFilters(
        recordsPerPage,
        currentPage,
        appliedSearchTerm,
        appliedSearchField,
        appliedCourseId,
        createdByFilter
      );
      
      const quizzesData = response.data || [];
      const pagination = response.pagination || {};
      
      // Enrich quiz data with course name and created by name
      const enrichedQuizzes = quizzesData.map((quiz) => {
        let courseName = 'Unknown Course';
        if (Array.isArray(quiz.courseId) && quiz.courseId.length > 0) {
          courseName = quiz.courseId[0]?.name || 'Unknown Course';
        } else if (quiz.courseId) {
          const course = coursesRef.current.find(c => c.id === quiz.courseId);
          courseName = course?.name || 'Unknown Course';
        }
        
        const mcqCount = Array.isArray(quiz.mcqIds) ? quiz.mcqIds.length : 0;
        
        return {
          ...quiz,
          courseName,
          mcqCount,
          createdByName: getCreatedByName(quiz.createdBy),
        };
      });
      
      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setData((prev) => {
          const seen = new Set(prev.map((q) => q.id));
          return [...prev, ...enrichedQuizzes.filter((q) => !seen.has(q.id))];
        });
      } else {
        setData(enrichedQuizzes);
      }
      appendRef.current = false;
      setTotalRecords(pagination.total || 0);
      console.log('Fetched data:', enrichedQuizzes.length, 'Total records:', pagination.total);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
      toast.error("Failed to fetch quizzes. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
      isFetching.current = false;
    }
  }, [recordsPerPage, currentPage, appliedSearchTerm, appliedSearchField, appliedCourseId, appliedTrainerId, isTrainer, actualTrainerId, isAdmin, getCreatedByName]);

  // Fetch quizzes whenever dependencies change
  useEffect(() => {
    if (trainerDataLoaded) {
      fetchQuizzes();
    }
  }, [fetchQuizzes, trainerDataLoaded]);

  // Initialize user profile
  useEffect(() => {
    const init = async () => {
      if (hasInitialized.current) return;
      hasInitialized.current = true;
      await fetchUserProfile();
    };
    init();
  }, [fetchUserProfile]);

  // Handle search with debounce
  const handleSearchChange = useMemo(
    () => debounce((value, field) => {
      setAppliedSearchTerm(value);
      setAppliedSearchField(field !== 'all' ? field : '');
      setCurrentPage(1);
    }, 500),
    []
  );

  const onSearchChange = (value, field) => {
    setUiSearchTerm(value);
    setUiSelectedField(field);
    handleSearchChange(value, field);
  };

  // Apply filters
  const handleApplyFilters = () => {
    appendRef.current = false;
    loadingMoreRef.current = false;
    setAppliedCourseId(uiCourseId);
    setAppliedTrainerId(uiTrainerId);
    setCurrentPage(1);
  };

  // Clear filters
  const handleClearFilters = () => {
    appendRef.current = false;
    loadingMoreRef.current = false;
    setUiCourseId('');
    setUiTrainerId('');
    setSelectedCourse(null);
    setSelectedTrainer(null);
    setAppliedCourseId('');
    setAppliedTrainerId('');
    setCurrentPage(1);
  };

  // Handle course filter change (UI only, not applied until Apply button clicked)
  const handleCourseChange = (selectedOption) => {
    setSelectedCourse(selectedOption);
    setUiCourseId(selectedOption ? selectedOption.value : '');
  };

  // Handle trainer filter change (UI only, not applied until Apply button clicked)
  const handleTrainerChange = (selectedOption) => {
    setSelectedTrainer(selectedOption);
    setUiTrainerId(selectedOption ? selectedOption.value : '');
  };

  const handleDelete = async (id) => {
    let hard = false;
    if (userRole === 'superadmin') {
      const res = await confirm({
        title: 'Delete Quiz',
        text: 'Choose delete type. Soft delete is reversible; Hard delete is permanent.',
        showDenyButton: true,
        confirmButtonText: 'Soft Delete',
        denyButtonText: 'Hard Delete',
        cancelButtonText: 'Cancel',
        tone: 'danger',
      });
      if (res.isDenied) hard = true;
      if (!res.isDenied && !res.isConfirmed) return;
    } else {
      const res = await confirm({
        title: 'Delete Confirmation',
        text: 'Are you sure you want to delete this quiz?',
        confirmButtonText: 'Yes, Delete it!',
        cancelButtonText: 'No, Cancel',
        tone: 'danger',
      });
      if (!res.isConfirmed) return;
    }

    try {
      await deleteQuiz(id);
      // Gone: the view popup, when it is open, says so in place of the
      // quiz and closes itself.
      const newTotalRecords = totalRecords - 1;
      const newTotalPages = Math.ceil(newTotalRecords / recordsPerPage);
      if (currentPage > newTotalPages && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchQuizzes();
      }
      /* The QUESTION above is already the house confirm; the ANSWER shows
         in the view popup (or is a toast for a delete from the list).

         What this replaced could not be themed even deliberately: its
         confirmButtonColor named --teal and --berry, which belong to the
         RETIRED css/theme/tokens.css layer and no longer move with the
         palette -- and customClass lms-popup made it moot anyway, because
         popups.css repaints any confirm button inside that class saffron
         with !important. */
      toast.success(
        hard ? 'Quiz deleted permanently' : 'Quiz deleted',
      );
    } catch (error) {
      console.error('Error deleting quiz:', error);
      toast.error('Could not delete the quiz. Try again.');
    }
  };

  const handleView = async (id) => {
    const quiz = data.find((item) => item.id === id);
    if (quiz) {
      setDetailsQuiz(quiz);
      setShowDetails(true);
    } else {
      toast.error('Quiz not found');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('DD/MM/YYYY HH:mm');
  };

  const getStatusBadge = (startDate, endDate) => {
    if (!startDate || !endDate) {
      return <span>Unknown</span>;
    }
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (now < start) {
      return <span>Scheduled</span>;
    } else if (now >= start && now <= end) {
      return <span>Active</span>;
    } else {
      return <span>Completed</span>;
    }
  };

  const headers = [
    {
      title: 'Quiz Title',
      prop: 'title',
      sortable: true,
      filterable: true,
      cell: (row) => (
        <span onClick={() => handleView(row.id)}>
          {row.title}
        </span>
      ),
    },
    {
      title: 'Course',
      prop: 'courseName',
      sortable: true,
      filterable: true,
      cell: (row) => <span>{row.courseName}</span>,
    },
    {
      title: 'MCQs',
      prop: 'mcqCount',
      sortable: true,
      cell: (row) => <span>{row.mcqCount} MCQs</span>,
    },
    {
      title: 'Duration',
      prop: 'time',
      sortable: true,
      cell: (row) => `${row.time || 0} min`,
    },
    {
      title: 'Status',
      prop: 'status',
      cell: (row) => getStatusBadge(row.startDate, row.endDate),
    },
  ];

  const indexedData = data.map((item, index) => ({
    ...item,
    srno: (currentPage - 1) * recordsPerPage + index + 1,
  }));

  // Handle page change
  const hasMoreQuizzes = data.length < totalRecords;

  // The next page arrives on scroll rather than on a page button.
  useEffect(() => {
    const node = listEndRef.current;
    if (!node || !hasMoreQuizzes || loading) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        // `loading` is state and lags a frame, so the ref is what stops
        // one pass asking for two pages.
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        appendRef.current = true;
        setLoadingMore(true);
        setCurrentPage((p) => p + 1);
      },
      { rootMargin: "150px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMoreQuizzes, loading, data.length]);

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (newLimit) => {
    appendRef.current = false;
    setRecordsPerPage(newLimit);
    setCurrentPage(1);
  };

  // Handle refresh button for dropdowns
  const handleCourseRefresh = async () => {
    setCourseSearch('');
    setCoursePage(1);
    setCourseHasMore(true);
    await fetchCourses(1, '', false);
  };

  const handleTrainerRefresh = async () => {
    setTrainerSearch('');
    setTrainerPage(1);
    setTrainerHasMore(true);
    await fetchTrainers(1, '', false);
  };

  if (loading && data.length === 0) {
    return (
      <div className="pg">
        <div className="pg-state">
          <span className="pg-spinner" aria-hidden="true" />
          <p className="pg-state-text">Loading quizzes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pg">

      <header className="pg-head pg-head--bare">
        <div className="pg-head-actions">
          {hasPermission('trainer-listquiz', 'add', 'lms') && (
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => {
                setFormStep(null);
                setFormModal({ quizId: null });
              }}
            >
              Create quiz
            </button>
          )}
        </div>
      </header>

      {/* Everything that narrows the list, search included -- the
          table's own toolbar is switched off below so the two are not
          stacked one above the other. */}
      <FilterSheet label="Filter quizzes">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="quiz-search">
              Search
            </label>
            <input
              id="quiz-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by quiz title, course or author..."
              value={uiSearchTerm}
              onChange={(e) => onSearchChange(e.target.value, uiSelectedField)}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="quiz-search-field">
              Search in
            </label>
            <select
              id="quiz-search-field"
              className="pg-select"
              value={uiSelectedField}
              onChange={(e) => {
                setUiSelectedField(e.target.value);
                onSearchChange(uiSearchTerm, e.target.value);
              }}
            >
              <option value="">All fields</option>
              <option value="title">Quiz title</option>
              <option value="courseName">Course</option>
              <option value="createdByName">Created by</option>
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="quiz-rows">
              Rows per load
            </label>
            <select
              id="quiz-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {isAdmin && (
          <div className="pg-filters-row">
            <div className="pg-filters-fields pg-filters-fields--notrainer">
              <div className="pg-field">
                <label className="pg-label">Trainer</label>
                <NewCustomAsyncSelectDropdown
                  options={trainerOptions}
                  value={selectedTrainer ? [selectedTrainer] : []}
                  onChange={(selectedOptions) => {
                    const option =
                      selectedOptions && selectedOptions.length > 0
                        ? selectedOptions[0]
                        : null;
                    handleTrainerChange(option);
                  }}
                  placeholder="All trainers"
                  isMulti={false}
                  isLoading={trainerLoading}
                  searchInControl={true}
                  onMenuOpen={async () => {
                    setTrainerPage(1);
                    setTrainerSearch('');
                    await fetchTrainers(1, '', false);
                  }}
                  onInputChange={debounce(async (searchValue) => {
                    setTrainerSearch(searchValue);
                    setTrainerPage(1);
                    await fetchTrainers(1, searchValue, false);
                  }, 500)}
                  onMenuScrollToBottom={async () => {
                    if (trainerHasMore && !trainerLoading) {
                      await fetchTrainers(trainerPage + 1, trainerSearch, true);
                    }
                  }}
                  onRefresh={handleTrainerRefresh}
                />
              </div>

              <div className="pg-field">
                <label className="pg-label">Course</label>
                <NewCustomAsyncSelectDropdown
                  options={courseOptions}
                  value={selectedCourse ? [selectedCourse] : []}
                  onChange={(selectedOptions) => {
                    const option =
                      selectedOptions && selectedOptions.length > 0
                        ? selectedOptions[0]
                        : null;
                    handleCourseChange(option);
                  }}
                  placeholder="All courses"
                  isMulti={false}
                  isLoading={courseLoading}
                  searchInControl={true}
                  onMenuOpen={async () => {
                    setCoursePage(1);
                    setCourseSearch('');
                    await fetchCourses(1, '', false);
                  }}
                  onInputChange={debounce(async (searchValue) => {
                    setCourseSearch(searchValue);
                    setCoursePage(1);
                    await fetchCourses(1, searchValue, false);
                  }, 500)}
                  onMenuScrollToBottom={async () => {
                    if (courseHasMore && !courseLoading) {
                      await fetchCourses(coursePage + 1, courseSearch, true);
                    }
                  }}
                  onRefresh={handleCourseRefresh}
                />
              </div>
            </div>

            <div className="pg-filters-actions">
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={handleApplyFilters}
              >
                Apply
              </button>
              <button
                type="button"
                className="pg-btn"
                onClick={handleClearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </FilterSheet>

      <div className="td-list td-list--quiz">
        <NewCustomDataTable
          data={indexedData}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => handleView(row.id)}
          // Every cell wraps its text, so the default ['TR','TD'] would
          // mean clicking the text itself did nothing. Nothing inside a
          // row handles its own click any more.
          validRowClickTagNames="*"
          rowProps={(row) => ({
            tabIndex: 0,
            role: 'button',
            'aria-label': `Open ${row.title || 'quiz'}`,
            onKeyDown: (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleView(row.id);
              }
            },
          })}
        />
      </div>

      {/* Where the reader is, and the sentinel the observer watches. */}
      <div className="att-picklist-foot" ref={listEndRef}>
        {loadingMore ? (
          <>
            <span className="pg-spinner att-spinner" aria-hidden="true" />
            <span>Loading more quizzes...</span>
          </>
        ) : hasMoreQuizzes ? (
          <span>
            {data.length} of {totalRecords} quizzes
          </span>
        ) : (
          totalRecords > 0 && (
            <span>
              All {totalRecords} quiz{totalRecords === 1 ? '' : 'zes'} loaded
            </span>
          )
        )}
      </div>

      {/* Details Modal */}
      {formModal && (
        <div
          className="pg-modal pg-modal--over"
          role="dialog"
          aria-modal="true"
          aria-labelledby="quiz-form-title"
          onClick={() => setFormModal(null)}
        >
          <div
            className="pg-modal-panel pg-modal-panel--xl pg-modal-panel--form"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">
                  {formModal.quizId ? 'Edit quiz' : 'Create quiz'}
                </span>
                <h3 className="pg-modal-title" id="quiz-form-title">
                  {formStep?.title ||
                    (formModal.quizId ? 'Edit quiz' : 'Create quiz')}
                </h3>
                <p className="pg-modal-sub">
                  {formStep?.hint ||
                    'Name it, schedule it, and pick its questions.'}
                </p>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setFormModal(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {formDone ? (
                <PopupDone message={formDone} onClose={() => setFormModal(null)} />
              ) : (
              <AddQuiz
                embedded
                quizId={formModal.quizId}
                onStepChange={(copy) => setFormStep(copy)}
                onCancel={() => setFormModal(null)}
                onDone={() => {
                  // Saved: the dialog shows the message in place of the form
                  // and closes itself a few seconds later. The form's own
                  // success message is the one shown; this is only for a save
                  // that raised none (created, but the batch assignment failed).
                  showFormDone('Quiz saved');
                  // The record changed, so the view popup still open behind this form
                  // would be showing the old one. It goes; the form stays.
                  setShowDetails(false);
                  appendRef.current = false;
                  setCurrentPage(1);
                  fetchQuizzes();
                }}
              />
              )}
            </div>
          </div>
        </div>
      )}

      {showDetails && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="quiz-details-title"
          onClick={() => setShowDetails(false)}
        >
          <div
            className="pg-modal-panel pg-modal-panel--wide"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Quiz</span>
                <h3 className="pg-modal-title" id="quiz-details-title">
                  {detailsQuiz?.title || "Quiz details"}
                </h3>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setShowDetails(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
          {detailsDone ? (
            <PopupDone message={detailsDone} onClose={() => setShowDetails(false)} />
          ) : detailsQuiz ? (
            <>
              {/* The facts, label above value, in a grid rather than
                  eight stacked "Label: value" lines. */}
              <dl className="quiz-facts">
                <div>
                  <dt>Course</dt>
                  <dd>{detailsQuiz.courseName || '—'}</dd>
                </div>
                <div>
                  <dt>Duration</dt>
                  <dd>{detailsQuiz.time} min</dd>
                </div>
                <div>
                  <dt>Total marks</dt>
                  <dd>{detailsQuiz.marks}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>{getStatusBadge(detailsQuiz.startDate, detailsQuiz.endDate)}</dd>
                </div>
                <div>
                  <dt>Starts</dt>
                  <dd>{formatDate(detailsQuiz.startDate)}</dd>
                </div>
                <div>
                  <dt>Ends</dt>
                  <dd>{formatDate(detailsQuiz.endDate)}</dd>
                </div>
                <div>
                  <dt>Created by</dt>
                  <dd>{detailsQuiz.createdByName || '—'}</dd>
                </div>
              </dl>

              <h5 className="pgm-section-title">
                Questions
                <span className="pgm-section-count">
                  {detailsQuiz.mcqCount || 0}
                </span>
              </h5>
              {detailsQuiz.mcqIds && detailsQuiz.mcqIds.length > 0 ? (
                /* The question first, its options beneath -- the same
                   shape the MCQ list uses, so a question reads the same
                   wherever it appears. */
                <ol className="mcq-qlist">
                  {detailsQuiz.mcqIds.map((mcq, index) => {
                    const options = [
                      mcq.option1,
                      mcq.option2,
                      mcq.option3,
                      mcq.option4,
                    ];
                    const correct = Number(mcq.correctAnswer);
                    return (
                      <li className="mcq-q" key={mcq.id || index}>
                        <p className="mcq-q-text">
                          <span className="mcq-q-n">{index + 1}</span>
                          {mcq.question || 'Untitled question'}
                        </p>
                        <div className="mcq-q-meta">
                          {options.map((opt, i) => {
                            const n = i + 1;
                            if (!opt) return null;
                            return (
                              <span
                                className={`mcq-opt${
                                  correct === n ? ' is-right' : ''
                                }`}
                                key={n}
                              >
                                <b>{String.fromCharCode(64 + n)}</b>
                                {opt}
                              </span>
                            );
                          })}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <p className="pgm-empty">No questions on this quiz yet.</p>
              )}
            </>
          ) : (
            <div className="pg-state">
              <span className="pg-spinner" aria-hidden="true" />
              <p className="pg-state-text">Loading quiz details...</p>
            </div>
          )}
            </div>

            {!detailsDone && (
            <footer className="pg-modal-foot">
              {/* What you can do with the quiz sits with Close, at the
                  end -- you read it first, then act on it. */}
              <div className="pgm-actions">
                {detailsQuiz &&
                  hasPermission('trainer-listquiz', 'update', 'lms') && (
                    <button
                      type="button"
                      className="pgm-btn"
                      onClick={() => {
                        setFormStep(null);
                        setFormModal({ quizId: detailsQuiz.id });
                      }}
                    >
                      Edit quiz
                    </button>
                  )}
                {detailsQuiz &&
                  hasPermission('trainer-listquiz', 'delete', 'lms') && (
                    <button
                      type="button"
                      className="pgm-btn pgm-btn--danger"
                      onClick={() => {
                        handleDelete(detailsQuiz.id);
                      }}
                    >
                      Delete quiz
                    </button>
                  )}
              </div>
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setShowDetails(false)}
              >
                Close
              </button>
            </footer>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ListQuiz;